import React, { useState, useRef } from 'react'
import { IntlShape } from 'react-intl'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { getAll, createOne, patchOne } from '../../../../../../services/common_requests'
import { TourPrice, PersonTypePrice } from '../../../core/_models'
import PricingForm from './PricingForm'

interface GeneralPricingProps {
  tourId: string
  intl: IntlShape
  onSaveComplete?: () => void
}

const GeneralPricing: React.FC<GeneralPricingProps> = ({ 
  tourId, 
  intl,
  onSaveComplete 
}) => {
  const queryClient = useQueryClient()
  const formRef = useRef<any>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Fetch tour pricing data
  const { data: pricingData, isLoading } = useQuery(
    ['tourPricing', tourId],
    () => getAll(null, `/tour-prices/by-tour/${tourId}`),
    {
      enabled: !!tourId,
    }
  )

  // Create pricing mutation
  const createPricingMutation = useMutation(
    (data: Omit<TourPrice, '_id' | 'createdAt' | 'updatedAt'>) =>
      createOne(data, '/tour-prices'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourPricing', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.PRICING.CREATE_SUCCESS' }))
        setHasChanges(false)
        if (onSaveComplete) onSaveComplete()
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PRICING.CREATE_ERROR' }))
      },
      onSettled: () => {
        setIsSaving(false)
      }
    }
  )

  // Update pricing mutation
  const updatePricingMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<TourPrice> }) =>
      patchOne(data, `/tour-prices/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourPricing', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.PRICING.UPDATE_SUCCESS' }))
        setHasChanges(false)
        if (onSaveComplete) onSaveComplete()
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PRICING.UPDATE_ERROR' }))
      },
      onSettled: () => {
        setIsSaving(false)
      }
    }
  )

  const handleSubmit = async (values: Partial<TourPrice>) => {
    if (!tourId) return;

    setIsSaving(true);

    // Ensure priceHT is set (use priceTTC if not provided)
    const priceHT = values.priceHT ?? values.priceTTC ?? 0;
    const priceTTC = values.priceTTC ?? 0;

    // Ensure personTypePrices have priceHT (use price if not provided)
    const personTypePrices = values.pricingMode === 'PER_PERSON'
      ? (values.personTypePrices || []).map(ptp => ({
          personTypeId: ptp.personTypeId,
          price: Number(ptp.price) || 0,
          priceHT: Number(ptp.price) || 0 // Using the same value for now as requested
        }))
      : [];

    const pricingPayload = {
      tour: tourId,
      priceHT,
      priceTTC,
      pricingMode: values.pricingMode,
      personTypePrices,
      pricingMarketingNote:
        typeof values.pricingMarketingNote === 'string'
          ? values.pricingMarketingNote.trim().slice(0, 2000)
          : undefined,
    };

    const existingPricing = pricingData?.data;
    if (existingPricing && existingPricing._id) {
      updatePricingMutation.mutate({
        id: existingPricing._id,
        data: pricingPayload,
      });
    } else {
      createPricingMutation.mutate(
        pricingPayload as Omit<TourPrice, '_id' | 'createdAt' | 'updatedAt'>
      );
    }
  }

  // Handle form changes
  const handleFormChange = () => {
    setHasChanges(true)
  }

  // Handle save button click
  const handleSave = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    } else {
      console.error("Form reference is not available!")
    }
  }

  // Handle cancel button click
  const handleCancel = () => {
    // Reset form to original values
    if (formRef.current) {
      formRef.current.resetForm()
    }
    setHasChanges(false)
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-h-300px'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  const currentPricing = pricingData?.data

  return (
    <div className='card card-flush'>
      <div id='pricing-form' className='card-body'>
        {tourId && (
          <PricingForm
            ref={formRef}
            initialValues={
              currentPricing
                ? {
                    ...currentPricing,
                    personTypePrices: (currentPricing.personTypePrices || []).map((ptp: any) => ({
                      personTypeId: ptp.personType?._id || '',
                      price: ptp.price,
                      priceHT: ptp.priceHT,
                    })),
                  }
                : { pricingMode: 'PER_PERSON', personTypePrices: [] }
            }
            onSubmit={handleSubmit}
            onChange={handleFormChange}
            intl={intl}
            tourId={tourId}
            isSubmitting={isSaving}
          />
        )}
        <div className='d-flex justify-content-end mt-4'>
          <button
            type='button'
            className='btn btn-light me-3'
            onClick={handleCancel}
          >
            {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? (
              <span className='spinner-border spinner-border-sm me-2' role='status'></span>
            ) : null}
            {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GeneralPricing 